import React, { useEffect, useMemo, useState } from "react";
import {
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import SecurityUserActionMenu from "./SecurityUserActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import { getUsersList } from "../../helpers/backend_helper";
import { postData } from "../../helpers/url-request";
import { showLoading } from "react-global-loading";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import UserDetail from "./UserDetail";
import UserPassword from "./UserPassword";
import "../Standards/Standards.scss";
import "./Security.scss";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
};

const SettingsUsers = ({
  createRequest = 0,
  embedded = false,
  onUsersLoaded,
}) => {
  const [userList, setUserList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [selectedMembershipFirmId, setSelectedMembershipFirmId] = useState(0);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [externalPasswordOpen, setExternalPasswordOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchUsers = () => {
    showLoading(true);
    getUsersList()
      .then((response) => {
        const users = response?.status === true ? response.data || [] : [];
        setUserList(users);
        onUsersLoaded?.(users);
      })
      .catch(() => {
        setUserList([]);
        onUsersLoaded?.([]);
      })
      .finally(() => showLoading(false));
  };
  const lifecycle = useAdminDataLifecycle(fetchUsers);

  useEffect(() => {
    fetchUsers();
  }, []);
  useEffect(() => {
    if (createRequest) openUser();
  }, [createRequest]);

  const openUser = (user) => {
    setSelectedUserId(user?.id || 0);
    setSelectedMembershipFirmId(user?.firmId || 0);
    setFormKey((value) => value + 1);
    setCanvasOpen(true);
  };

  const closeUser = () => {
    setCanvasOpen(false);
    setSelectedUserId(0);
    fetchUsers();
  };

  const openPassword = (user) => {
    setSelectedUserId(user.id);
    setPasswordOpen(true);
  };

  const openExternalPassword = (user) => {
    setSelectedUserId(user.id);
    setExternalPasswordOpen(true);
  };

  const alignProfileEmail = async (user) => {
    const loginName = user.userLogin?.loginName?.trim();
    if (!loginName)
      return ErrorNotify("This user does not have an active login email.");
    showLoading(true);
    try {
      const response = await postData("/user/updateuser", {
        userId: user.id,
        fullName: user.fullName,
        emailId: loginName,
        mobileNo: user.mobileNo,
        blockUser: user.isBlocked || 0,
        changePassword: user.changePassword || 0,
        disableUser: user.status === 1 ? 0 : 1,
      });
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to align the profile email."
        );
      SuccessNotify(
        `${user.fullName}'s Entra email now matches the PIERS login.`
      );
      fetchUsers();
    } catch (error) {
      ErrorNotify(error.message || "Unable to align the profile email.");
    } finally {
      showLoading(false);
    }
  };

  const provisionExternalIdentity = async (user) => {
    showLoading(true);
    try {
      const response = await postData(`/user/provisionexternal/${user.id}`, {});
      if (!response?.status)
        throw new Error(
          response?.message || "External identity provisioning failed."
        );
      SuccessNotify(
        response.message === "already_provisioned"
          ? `${user.fullName} is already provisioned in Microsoft Entra.`
          : `${user.fullName} was provisioned in Microsoft Entra.`
      );
    } catch (error) {
      ErrorNotify(error.message || "External identity provisioning failed.");
    } finally {
      showLoading(false);
    }
  };

  const roleOptions = useMemo(
    () =>
      [
        ...new Set(
          userList
            .map(
              (user) => user.role?.name || user.roleName || user.userRole?.name
            )
            .filter(Boolean)
        ),
      ].sort(),
    [userList]
  );
  const filteredUsers = useMemo(
    () =>
      userList.filter((user) => {
        const role =
          user.role?.name || user.roleName || user.userRole?.name || "";
        const status =
          user.status === 1 || user.status === true ? "active" : "inactive";
        const haystack = [
          user.fullName,
          user.userLogin?.loginName,
          role,
          user.locationDetail?.name,
          user.designationDetail?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return (
          haystack.includes(search.toLowerCase()) &&
          (roleFilter === "all" || role === roleFilter) &&
          (statusFilter === "all" || status === statusFilter)
        );
      }),
    [roleFilter, search, statusFilter, userList]
  );

  const columns = [
    {
      id: "fullName",
      header: "User",
      accessorKey: "fullName",
      minSize: 210,
      flex: 1.25,
      cell: ({ row, getValue }) => {
        const name = getValue() || "Unnamed user";
        const initials = name
          .split(" ")
          .filter(Boolean)
          .map((part) => part[0])
          .join("")
          .slice(0, 2)
          .toUpperCase();
        return (
          <button
            type="button"
            className="security-users__name"
            onClick={() => openUser(row.original)}
          >
            <span>{initials}</span>
            {name}
          </button>
        );
      },
    },
    {
      id: "role",
      header: "Role",
      accessorFn: (user) =>
        user.role?.name || user.roleName || user.userRole?.name || "—",
      minSize: 145,
      flex: 0.9,
    },
    {
      id: "location",
      header: "Location",
      accessorFn: (user) =>
        user.locationDetail?.name || user.locationName || "—",
      minSize: 135,
      flex: 0.8,
    },
    {
      id: "designation",
      header: "Designation",
      accessorFn: (user) =>
        user.designationDetail?.name || user.designation || "—",
      minSize: 145,
      flex: 0.85,
    },
    {
      id: "email",
      header: "Email",
      accessorFn: (user) =>
        user.userLogin?.loginName || user.emailId || user.email || "—",
      minSize: 210,
      flex: 1.1,
    },
    {
      id: "lastLogin",
      header: "Last Login",
      accessorFn: (user) => formatDate(user.lastLogin),
      minSize: 130,
      flex: 0.75,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (user) =>
        user.status === 1 || user.status === true ? "Active" : "Inactive",
      minSize: 105,
      flex: 0.55,
      cell: ({ getValue }) => (
        <span
          className={`standards-grid__status ${
            getValue() === "Active"
              ? "standards-grid__status--active"
              : "standards-grid__status--archived"
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 90,
      maxSize: 90,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <SecurityUserActionMenu
          user={row.original}
          onEdit={openUser}
          onResetPassword={openPassword}
          onSetExternalPassword={openExternalPassword}
          onAlignProfileEmail={alignProfileEmail}
          onProvisionExternalIdentity={provisionExternalIdentity}
          lifecycleActions={lifecycle.actions("user", row.original)}
        />
      ),
    },
  ];
  const visibleColumns = embedded
    ? columns.filter((column) =>
        ["fullName", "role", "email", "status", "actions"].includes(column.id)
      )
    : columns;

  return (
    <section
      className={`security-users${embedded ? " security-users--embedded" : ""}`}
    >
      <div className="security-users__grid">
        <CustomDataGrid
 filterContent={<div className="security-users__filters">
          <label className="security-users__filter">
            <span>Search</span>
            <div>
              <i className="bx bx-search" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search users..."
              />
            </div>
          </label>
          <label className="security-users__select">
            <span>Role</span>
            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <option value="all">All Roles</option>
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
          <label className="security-users__select">
            <span>Status</span>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
        <button
          type="button"
          onClick={() => {
            setSearch("");
            setRoleFilter("all");
            setStatusFilter("all");
          }}
        >
          Clear
        </button>
        </div>}

          data={filteredUsers}
          columns={visibleColumns}
          onRefresh={fetchUsers}
          fileName="users"
          recordLabel="users"
          showToolbar={false}
          initialPageSize={embedded ? 50 : 15}
          rowHeight={embedded ? 56 : 64}
          columnHeaderHeight={52}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".15px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              borderBottomColor: "#edf0f5",
              color: "#273957",
              fontSize: 13,
            },
            "& .MuiDataGrid-row": {
              minHeight: `${embedded ? 56 : 64}px !important`,
            },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              { borderTopColor: "#e7ebf2", fontSize: 14 },
          }}
        />
      </div>
      {lifecycle.dialog}

      <Offcanvas
        backdrop="static"
        className="standards-drawer standards-drawer--wide"
        direction="end"
        isOpen={canvasOpen}
        toggle={() => setCanvasOpen(false)}
        keyboard={false}
      >
        <OffcanvasHeader
          toggle={() => setCanvasOpen(false)}
          className="canvasHeader"
        >
          {selectedUserId ? "Edit User" : "Invite User"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <UserDetail
            key={formKey}
            userId={selectedUserId}
            firmId={selectedMembershipFirmId}
            onEditUser={openUser}
            closeAction={closeUser}
          />
        </OffcanvasBody>
      </Offcanvas>
      <Offcanvas
        backdrop="static"
        className="w-30"
        direction="end"
        isOpen={externalPasswordOpen}
        toggle={() => setExternalPasswordOpen(false)}
      >
        <OffcanvasHeader
          toggle={() => setExternalPasswordOpen(false)}
          className="canvasHeader"
        >
          Reset Password
        </OffcanvasHeader>
        <OffcanvasBody>
          <UserPassword
            key={`entra-${selectedUserId}-${formKey}`}
            userId={selectedUserId}
            closeAction={() => setExternalPasswordOpen(false)}
            external
          />
        </OffcanvasBody>
      </Offcanvas>
      <Offcanvas
        backdrop="static"
        className="w-30"
        direction="end"
        isOpen={passwordOpen}
        toggle={() => setPasswordOpen(false)}
      >
        <OffcanvasHeader
          toggle={() => setPasswordOpen(false)}
          className="canvasHeader"
        >
          Reset Password
        </OffcanvasHeader>
        <OffcanvasBody>
          <UserPassword
            key={formKey}
            userId={selectedUserId}
            closeAction={() => setPasswordOpen(false)}
          />
        </OffcanvasBody>
      </Offcanvas>
    </section>
  );
};

export default SettingsUsers;
